import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { X, Upload, AlertCircle, CheckCircle, Download } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showError, showWarning, showSuccess } from "@/utils/toastHelpers";
import {
  transformStockErrorsToExcelRows,
  getStockErrorReportColumnWidths,
} from "@/utils/stockUploadHelpers";

export default function BulkStockUpload({ onClose }) {
  const queryClient = useQueryClient();

  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Helper to convert Excel date to YYYY-MM-DD format if needed
  const normalizeDate = (dateValue) => {
    if (!dateValue) return dateValue;

    // If it's already a string in YYYY-MM-DD format, return as-is
    if (
      typeof dateValue === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
      return dateValue;
    }

    // If it's a Date object or parseable date string
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        // Convert to YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // Fall through to return original value
    }

    return dateValue;
  };

  // Helper to get value from row with flexible key matching (handles various column name formats)
  const getRowValue = (row, ...possibleKeys) => {
    for (const key of possibleKeys) {
      // Check exact match first
      if (row[key] !== undefined) return row[key];
      // Check case-insensitive and underscore/space variations
      const normalizedKey = key.toLowerCase().replace(/[_\s]/g, "");
      for (const rowKey of Object.keys(row)) {
        const normalizedRowKey = rowKey.toLowerCase().replace(/[_\s]/g, "");
        if (normalizedRowKey === normalizedKey) {
          return row[rowKey];
        }
      }
    }
    return "";
  };

  // Check if a row has stock data (matching backend logic - only current_stock is required)
  const hasStockData = (row) => {
    const stock = getRowValue(row, "current_stock", "currentStock");
    return stock !== undefined && stock !== null && stock !== "";
  };

  // Get rows that will actually be uploaded (have stock data)
  const rowsToUpload = parsedData.filter(hasStockData);

  const downloadTemplate = async (format = "csv") => {
    setIsDownloadingTemplate(true);
    try {
      const response = await apiClient.get(ENDPOINTS.INVENTORY.STOCK_TEMPLATE);

      if (format === "xlsx") {
        // Download as Excel using XLSX library
        const XLSX = await import(
          "https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs"
        );

        // Create worksheet data with headers
        const wsData = [
          response.columns,
          ...response.data.map((row) =>
            response.columns.map((col) => row[col] || "")
          ),
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock Template");

        // Generate Excel file and download
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          response.filename.replace(/\.(json|csv)$/, ".xlsx")
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Download as CSV
        const headers = response.columns.join(",");
        const rows = response.data.map((row) =>
          response.columns
            .map((col) => {
              const value = row[col] || "";
              // Escape commas and quotes in values
              if (value.includes(",") || value.includes('"')) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(",")
        );

        const csvContent = [headers, ...rows].join("\n");

        // Create and download the file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          response.filename.replace(/\.(json)$/, ".csv")
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      showSuccess(
        "Template downloaded",
        `Contains ${response.totalMedicines} medicines`
      );
    } catch (error) {
      console.error("Failed to download template:", error);
      showError(
        "Failed to download template",
        error.message || "Please try again"
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const uploadMutation = useMutation({
    mutationFn: (stockItems) =>
      apiClient.post(ENDPOINTS.INVENTORY.BULK_UPLOAD, { stockItems }),
    onSuccess: (response) => {
      setUploadResult(response);
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      queryClient.invalidateQueries(["lowStockAlerts"]);
      queryClient.invalidateQueries(["expiringItems"]);

      if (response.success > 0 && response.failed === 0) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    },
    onError: (error) => {
      console.error("Failed to upload stock:", error);
      showError(
        "Failed to upload stock",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParsedData([]);
      setParseError(null);
      setUploadResult(null);
      parseFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    setIsParsing(true);
    setParseError(null);

    if (fileExtension === "csv") {
      parseCSV(file);
    } else if (fileExtension === "xlsx" || fileExtension === "xls") {
      parseExcel(file);
    } else {
      setIsParsing(false);
      setParseError(
        "Unsupported file format. Please upload a CSV or Excel file."
      );
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());

        const data = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === "") continue;

          const values = lines[i].split(",").map((v) => v.trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });

          // Send all rows to backend - let backend handle validation
          data.push(row);
        }
        setParsedData(data);
        setIsParsing(false);

        if (data.length === 0) {
          setParseError("No data rows found in CSV file.");
        }
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setParseError(
          "Failed to parse CSV file. Please check the file format."
        );
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file. Please try again.");
      setIsParsing(false);
    };
    reader.readAsText(file);
  };

  const parseExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Using a simple approach - load XLSX dynamically
        import("https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs")
          .then((XLSX) => {
            try {
              const data = new Uint8Array(e.target.result);
              const workbook = XLSX.read(data, { type: "array" });

              // Get first sheet
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];

              // Convert to JSON - send all rows to backend for validation
              // Use raw:false to keep date formatting, but handle date conversion
              const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                raw: false, // Convert dates to strings
                dateNF: "yyyy-mm-dd", // Format dates as YYYY-MM-DD
              });

              // Normalize date fields to ensure consistent YYYY-MM-DD format
              const normalizedData = jsonData.map((row) => {
                const normalized = { ...row };
                // Check for expiry_date or expiryDate fields and normalize
                if (row.expiry_date) {
                  normalized.expiry_date = normalizeDate(row.expiry_date);
                }
                if (row.expiryDate) {
                  normalized.expiryDate = normalizeDate(row.expiryDate);
                }
                if (row["Expiry Date"]) {
                  normalized["Expiry Date"] = normalizeDate(row["Expiry Date"]);
                }
                return normalized;
              });

              setParsedData(normalizedData);
              setIsParsing(false);

              if (jsonData.length === 0) {
                setParseError("No data rows found in Excel file.");
              }
            } catch (error) {
              console.error("Error processing Excel data:", error);
              setParseError(
                "Failed to process Excel file. Please ensure it is a valid Excel file."
              );
              setIsParsing(false);
            }
          })
          .catch((error) => {
            console.error("Error loading Excel parser:", error);
            setParseError(
              "Failed to load Excel parser. Please try again or use CSV format."
            );
            setIsParsing(false);
          });
      } catch (error) {
        console.error("Error parsing Excel:", error);
        setParseError(
          "Failed to parse Excel file. Please ensure it is a valid Excel file."
        );
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file. Please try again.");
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = () => {
    if (rowsToUpload.length === 0) {
      showWarning("No rows with stock data to upload");
      return;
    }

    uploadMutation.mutate(parsedData);
  };

  const downloadErrorReport = async () => {
    if (!uploadResult || !uploadResult.errors || uploadResult.errors.length === 0) {
      showWarning("No errors to download");
      return;
    }

    try {
      // Dynamically import xlsx
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs");

      // Transform errors to Excel rows using utility function
      const errorRows = transformStockErrorsToExcelRows(uploadResult.errors);

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(errorRows);

      // Set column widths using utility function
      worksheet['!cols'] = getStockErrorReportColumnWidths();

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `stock-upload-errors-${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      showSuccess("Error report downloaded successfully");
    } catch (error) {
      console.error("Error generating report:", error);
      showError("Failed to generate error report", "Please try again");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-900">
            Bulk Upload Stock
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Step 1: Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3">
              Step 1: Download Template
            </h3>
            <p className="text-sm text-blue-800 mb-3">
              Download a template containing all your medicines with their IDs.
              Fill in the stock details for the medicines you want to upload.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => downloadTemplate("csv")}
                disabled={isDownloadingTemplate}
                variant="outline"
                className="bg-white"
              >
                {isDownloadingTemplate ? (
                  <>
                    <Download className="w-4 h-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </>
                )}
              </Button>
              <Button
                onClick={() => downloadTemplate("xlsx")}
                disabled={isDownloadingTemplate}
                variant="outline"
                className="bg-white"
              >
                {isDownloadingTemplate ? (
                  <>
                    <Download className="w-4 h-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Excel
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Step 2: Instructions */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-900 mb-2">
              Step 2: Fill In Your Data
            </h3>
            <p className="text-sm text-slate-700 mb-2">
              Open the template in Excel or Google Sheets and fill in these
              columns for each medicine you want to add stock for:
            </p>
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              <li>
                <strong>expiry_date</strong>: Format as YYYY-MM-DD e.g.
                2025-12-31 (required)
              </li>
              <li>
                <strong>current_stock</strong>: Quantity received (required)
              </li>
              <li>
                <strong>unit_cost</strong>: Cost per unit (required)
              </li>
              <li>
                <strong>batch_number</strong>: Batch/lot number (required)
              </li>
              <li>
                <strong>supplier</strong>: Supplier name (required)
              </li>
              <li>storage_location, notes (optional)</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <strong>Important:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>
                  Do NOT modify the medicine_id, company, product, strength, or
                  pack_size columns
                </li>
                <li>
                  Leave rows empty for medicines you don&apos;t want to upload -
                  they will be skipped
                </li>
                <li>
                  Column headers are flexible (case and spacing don&apos;t
                  matter)
                </li>
              </ul>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select CSV or Excel File
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={isParsing}
                className="flex-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
              />
            </div>
            {file && (
              <p className="mt-2 text-sm text-slate-600">
                Selected: {file.name}
              </p>
            )}
            {isParsing && (
              <p className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                <Upload className="w-4 h-4 animate-spin" />
                Parsing file...
              </p>
            )}
          </div>

          {/* Parse Error */}
          {parseError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-1">
                    Parsing Error
                  </h4>
                  <p className="text-sm text-red-800">{parseError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {parsedData.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Preview ({rowsToUpload.length} stock item
                {rowsToUpload.length !== 1 ? "s" : ""} to upload)
              </h3>
              {rowsToUpload.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  No rows have stock data filled in. Please add current_stock
                  values to the rows you want to upload.
                </div>
              ) : (
                <>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Medicine ID
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Batch
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Expiry
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Stock
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Cost
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                              Supplier
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {rowsToUpload.slice(0, 10).map((row, index) => (
                            <tr key={index} className="hover:bg-slate-50">
                              <td className="px-3 py-2">
                                {getRowValue(row, "medicine_id", "medicineId")}
                              </td>
                              <td className="px-3 py-2">
                                {getRowValue(row, "batch_number", "batchNumber")}
                              </td>
                              <td className="px-3 py-2">
                                {getRowValue(row, "expiry_date", "expiryDate")}
                              </td>
                              <td className="px-3 py-2">
                                {getRowValue(row, "current_stock", "currentStock")}
                              </td>
                              <td className="px-3 py-2">
                                {getRowValue(row, "unit_cost", "unitCost")
                                  ? `R${getRowValue(row, "unit_cost", "unitCost")}`
                                  : ""}
                              </td>
                              <td className="px-3 py-2">
                                {getRowValue(row, "supplier") || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {rowsToUpload.length > 10 && (
                      <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 text-center border-t border-slate-200">
                        Showing first 10 of {rowsToUpload.length} stock items
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div
              className={`rounded-lg p-4 ${
                uploadResult.failed === 0
                  ? "bg-green-50 border border-green-200"
                  : "bg-yellow-50 border border-yellow-200"
              }`}
            >
              <div className="flex items-start gap-3">
                {uploadResult.failed === 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4
                    className={`font-semibold mb-1 ${
                      uploadResult.failed === 0
                        ? "text-green-900"
                        : "text-yellow-900"
                    }`}
                  >
                    Upload Complete
                  </h4>
                  <p
                    className={`text-sm ${
                      uploadResult.failed === 0
                        ? "text-green-800"
                        : "text-yellow-800"
                    }`}
                  >
                    Successfully uploaded: {uploadResult.success} stock items
                    {uploadResult.failed > 0 &&
                      ` | Failed: ${uploadResult.failed}`}
                    {uploadResult.skipped > 0 &&
                      ` | Skipped: ${uploadResult.skipped} empty rows`}
                  </p>

                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-yellow-900">
                          Errors:
                        </p>
                        <Button
                          onClick={downloadErrorReport}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Error Report
                        </Button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {uploadResult.errors.slice(0, 5).map((error, index) => (
                          <div
                            key={index}
                            className="text-xs bg-white/50 p-2 rounded border border-yellow-300"
                          >
                            <span className="font-semibold">
                              Row {error.row}:
                            </span>{" "}
                            {error.error}
                          </div>
                        ))}
                      </div>
                      {uploadResult.errors.length > 5 && (
                        <p className="text-xs text-yellow-700 mt-2">
                          ... and {uploadResult.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" onClick={onClose} variant="outline">
              {uploadResult && uploadResult.failed === 0 ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                uploadMutation.isPending ||
                isParsing ||
                rowsToUpload.length === 0 ||
                uploadResult
              }
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {uploadMutation.isPending ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : isParsing ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {rowsToUpload.length} Stock Item
                  {rowsToUpload.length !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
